'use client';
import { useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import Link from 'next/link';

export default function DataRetentionPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <SectionHeader
              eyebrow="Legal"
              title="Data Retention Policy"
              description="Last updated: February 18, 2026"
              align="center"
              className="mb-16"
            />
          </div>
        </section>

        <section className="py-24 px-6">
          <div className="max-w-[900px] mx-auto">
            <div className="space-y-12">
              {/* Introduction */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">Introduction</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  This Data Retention Policy explains how long Best Odds Corp retains personal information collected through our services, including data collected via social sign-in providers (Google, Facebook), and the criteria we use to determine retention periods.
                </p>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  This policy complies with applicable data protection laws and the requirements of third-party authentication providers. For general privacy information, please see our <Link href="/legal/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
                </p>
              </div>

              {/* Retention Principles */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">1. Data Retention Principles</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We retain personal information only for as long as necessary to:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-4">
                  <li>Fulfill the purposes for which it was collected</li>
                  <li>Provide and maintain our services</li>
                  <li>Comply with legal, regulatory, tax, and accounting obligations</li>
                  <li>Resolve disputes and enforce our agreements</li>
                  <li>Protect against fraudulent, abusive, or illegal activity</li>
                  <li>Maintain business records and analytics</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  When personal information is no longer needed for these purposes, we securely delete or anonymize it in accordance with this policy.
                </p>
              </div>

              {/* Specific Retention Periods */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">2. Specific Retention Periods</h2>
                
                <h3 className="text-lg font-semibold mb-3 text-primary">2.1 Account and Profile Information</h3>
                <div className="mb-6 bg-background/50 p-4 rounded border border-border/50">
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Retention Period:</strong> Duration of active account + 3 years after account closure or last activity</p>
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Data Types:</strong> Name, email address, phone number, company information, profile details, social sign-in credentials (Google, Facebook)</p>
                  <p className="text-body-sm text-muted-foreground"><strong>Justification:</strong> Required to maintain service continuity, comply with tax and accounting regulations, and resolve potential disputes or claims</p>
                </div>

                <h3 className="text-lg font-semibold mb-3 text-primary">2.2 Social Sign-In Data (Google, Facebook)</h3>
                <div className="mb-6 bg-background/50 p-4 rounded border border-border/50">
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Retention Period:</strong> Duration of active account + 90 days after account deletion request</p>
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Data Types:</strong> OAuth tokens, social profile identifiers, email address from social provider, profile picture URL, public profile information</p>
                  <p className="text-body-sm text-muted-foreground mb-4"><strong>Justification:</strong> Required for authentication, account recovery, and compliance with Google and Facebook platform policies</p>
                  <p className="text-body-sm text-muted-foreground italic"><strong>Note:</strong> OAuth access tokens are automatically revoked upon account deletion. Refresh tokens are deleted within 90 days. We do not store social media passwords.</p>
                </div>

                <h3 className="text-lg font-semibold mb-3 text-primary">2.3 Transaction and Payment Records</h3>
                <div className="mb-6 bg-background/50 p-4 rounded border border-border/50">
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Retention Period:</strong> 7 years from date of transaction</p>
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Data Types:</strong> Invoices, payment records, billing addresses, transaction history, purchase orders</p>
                  <p className="text-body-sm text-muted-foreground"><strong>Justification:</strong> Required by tax laws, accounting standards, and financial regulations. Essential for audit trails and dispute resolution</p>
                </div>

                <h3 className="text-lg font-semibold mb-3 text-primary">2.4 Project and Service Delivery Data</h3>
                <div className="mb-6 bg-background/50 p-4 rounded border border-border/50">
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Retention Period:</strong> Duration of project + 5 years after project completion</p>
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Data Types:</strong> Project briefs, deliverables, communications, work product, client feedback, contracts, statements of work</p>
                  <p className="text-body-sm text-muted-foreground"><strong>Justification:</strong> Required for warranty obligations, intellectual property protection, professional liability defense, and business continuity</p>
                </div>

                <h3 className="text-lg font-semibold mb-3 text-primary">2.5 Marketing and Communication Data</h3>
                <div className="mb-6 bg-background/50 p-4 rounded border border-border/50">
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Retention Period:</strong> Until opt-out/unsubscribe + 30 days for processing</p>
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Data Types:</strong> Email subscription preferences, newsletter history, marketing campaign interactions, consent records</p>
                  <p className="text-body-sm text-muted-foreground"><strong>Justification:</strong> Required to honor opt-out requests and maintain suppression lists to prevent unwanted communications</p>
                </div>

                <h3 className="text-lg font-semibold mb-3 text-primary">2.6 Website Analytics and Usage Data</h3>
                <div className="mb-6 bg-background/50 p-4 rounded border border-border/50">
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Retention Period:</strong> 26 months from collection date</p>
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Data Types:</strong> IP addresses, browser information, page views, session data, cookie identifiers, device information</p>
                  <p className="text-body-sm text-muted-foreground"><strong>Justification:</strong> Required for website optimization, security monitoring, and compliance with Google Analytics data retention policies</p>
                </div>

                <h3 className="text-lg font-semibold mb-3 text-primary">2.7 Customer Support and Communications</h3>
                <div className="mb-6 bg-background/50 p-4 rounded border border-border/50">
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Retention Period:</strong> 3 years from last communication</p>
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Data Types:</strong> Support tickets, email correspondence, chat logs, phone call records, feedback submissions</p>
                  <p className="text-body-sm text-muted-foreground"><strong>Justification:</strong> Required for quality assurance, dispute resolution, and continuous service improvement</p>
                </div>

                <h3 className="text-lg font-semibold mb-3 text-primary">2.8 Security and Fraud Prevention Data</h3>
                <div className="mb-6 bg-background/50 p-4 rounded border border-border/50">
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Retention Period:</strong> 5 years from incident date or resolution</p>
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Data Types:</strong> Security logs, access logs, authentication attempts, fraud detection data, incident reports</p>
                  <p className="text-body-sm text-muted-foreground"><strong>Justification:</strong> Required for security monitoring, fraud investigation, legal proceedings, and regulatory compliance</p>
                </div>

                <h3 className="text-lg font-semibold mb-3 text-primary">2.9 Legal and Compliance Records</h3>
                <div className="mb-6 bg-background/50 p-4 rounded border border-border/50">
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Retention Period:</strong> Duration of legal obligation + applicable statute of limitations (typically 7-10 years)</p>
                  <p className="text-body-sm text-muted-foreground mb-2"><strong>Data Types:</strong> Contracts, legal notices, compliance documentation, consent records, data processing agreements</p>
                  <p className="text-body-sm text-muted-foreground"><strong>Justification:</strong> Required by law, regulatory requirements, and for legal defense purposes</p>
                </div>
              </div>

              {/* Data Deletion Procedures */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">3. Data Deletion Procedures</h2>
                
                <h3 className="text-lg font-semibold mb-3 text-primary">3.1 Automated Deletion</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We implement automated systems to identify and delete personal information that has exceeded its retention period. These systems run on a regular schedule and include:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-6">
                  <li>Monthly reviews of account activity and retention status</li>
                  <li>Automated deletion of expired session data and temporary files</li>
                  <li>Quarterly purges of marketing data for unsubscribed users</li>
                  <li>Annual reviews of archived project data and communications</li>
                </ul>

                <h3 className="text-lg font-semibold mb-3 text-primary">3.2 User-Requested Deletion</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  You may request deletion of your personal information at any time by:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-4">
                  <li>Submitting a deletion request through our <Link href="/contact" className="text-primary hover:underline">contact form</Link></li>
                  <li>Emailing us at <a href="mailto:hello@bestoddscorp.com" className="text-primary hover:underline">hello@bestoddscorp.com</a></li>
                  <li>Deleting your account through account settings (if applicable)</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-6">
                  We will process deletion requests within 30 days, subject to legal retention requirements. Some information may be retained in backup systems for up to 90 additional days.
                </p>

                <h3 className="text-lg font-semibold mb-3 text-primary">3.3 Secure Deletion Methods</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  When deleting personal information, we use secure methods including:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4">
                  <li>Permanent deletion from active databases and systems</li>
                  <li>Overwriting of deleted data to prevent recovery</li>
                  <li>Removal from backup systems within 90 days</li>
                  <li>Anonymization of data required for statistical or analytical purposes</li>
                  <li>Secure destruction of physical records containing personal information</li>
                </ul>
              </div>

              {/* Exceptions to Deletion */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">4. Exceptions to Deletion</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We may retain certain personal information beyond the standard retention periods when:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-4">
                  <li><strong>Legal Obligation:</strong> Required by law, regulation, or court order</li>
                  <li><strong>Pending Litigation:</strong> Subject to ongoing or anticipated legal proceedings</li>
                  <li><strong>Fraud Investigation:</strong> Involved in active fraud or security investigations</li>
                  <li><strong>Debt Collection:</strong> Related to outstanding payments or financial obligations</li>
                  <li><strong>Contractual Obligation:</strong> Required under existing contracts or service agreements</li>
                  <li><strong>Legitimate Business Interest:</strong> Necessary for compelling business purposes that override deletion requests</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  In such cases, we will inform you of the reason for extended retention and the expected retention period.
                </p>
              </div>

              {/* Social Sign-In Compliance */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">5. Social Sign-In Provider Compliance</h2>
                
                <h3 className="text-lg font-semibold mb-3 text-primary">5.1 Google Sign-In Compliance</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  Our data retention practices comply with Google's OAuth 2.0 policies and requirements:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-6">
                  <li>We only request access to Google user data necessary for authentication and service delivery</li>
                  <li>Google OAuth tokens are stored securely and encrypted</li>
                  <li>Access tokens are automatically revoked upon account deletion</li>
                  <li>We do not share Google user data with third parties except as disclosed in our Privacy Policy</li>
                  <li>Users can revoke access at any time through their Google Account settings</li>
                </ul>

                <h3 className="text-lg font-semibold mb-3 text-primary">5.2 Facebook Login Compliance</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  Our data retention practices comply with Facebook Platform Policies:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-6">
                  <li>We only request Facebook permissions necessary for authentication</li>
                  <li>Facebook user data is deleted within 90 days of account deletion or access revocation</li>
                  <li>We comply with Facebook's data deletion callback requirements</li>
                  <li>Users can manage permissions and delete data through Facebook settings</li>
                  <li>We provide a data deletion status check endpoint as required by Facebook</li>
                </ul>

                <h3 className="text-lg font-semibold mb-3 text-primary">5.3 Revoking Social Sign-In Access</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  You can revoke our access to your social sign-in data at any time:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4">
                  <li><strong>Google:</strong> Visit <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Account Permissions</a> and remove Best Odds Corp</li>
                  <li><strong>Facebook:</strong> Visit <a href="https://www.facebook.com/settings?tab=applications" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Facebook Apps and Websites</a> and remove Best Odds Corp</li>
                  <li><strong>Our Platform:</strong> Delete your account through account settings or contact us directly</li>
                </ul>
              </div>

              {/* Data Anonymization */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">6. Data Anonymization</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  When personal information is no longer needed for its original purpose but has value for statistical, analytical, or research purposes, we may anonymize it. Anonymized data:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-4">
                  <li>Cannot be used to identify individuals</li>
                  <li>Is aggregated with other data to prevent re-identification</li>
                  <li>Has all direct and indirect identifiers removed</li>
                  <li>May be retained indefinitely for business intelligence and service improvement</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  Anonymized data is not considered personal information and is not subject to data subject rights or deletion requests.
                </p>
              </div>

              {/* Updates to Policy */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">7. Updates to This Policy</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We may update this Data Retention Policy to reflect changes in our practices, legal requirements, or business operations. Material changes will be communicated through:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-4">
                  <li>Email notification to registered users</li>
                  <li>Prominent notice on our website</li>
                  <li>Updated "Last updated" date at the top of this policy</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  Continued use of our services after policy updates constitutes acceptance of the revised retention practices.
                </p>
              </div>

              {/* Contact */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">8. Contact Us</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  If you have questions about our data retention practices or wish to request deletion of your personal information, please contact us:
                </p>
                <div className="space-y-2 text-body-sm text-muted-foreground">
                  <p><strong>Best Odds Corp</strong></p>
                  <p>4575 Dean Martin Dr. Unit 1501</p>
                  <p>Las Vegas, NV 89141</p>
                  <p>USA</p>
                  <p className="mt-4">Email: <a href="mailto:hello@bestoddscorp.com" className="text-primary hover:underline">hello@bestoddscorp.com</a></p>
                  <p>Phone: <a href="tel:+17025466337" className="text-primary hover:underline">(702) 546-6337</a></p>
                  <p className="mt-4">Contact Form: <Link href="/contact" className="text-primary hover:underline">bestoddscorp.com/contact</Link></p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}